'use client';

import { useEffect, useState } from 'react';
import DocumentsList from './DocumentsList';
import Preview from './Preview';
import MandateWorkflow from './MandateWorkflow';
import { useGetDocumentsQuery } from '@/redux/services/brokerService';
import { useGetAllPropertyDocumentsQuery } from '@/redux/services/propertyService';

const DocumentsAndPreview = ({ methods, onSubmit, onDocumentsChange, type, brokerId, documentPropertyById, sampleType }) => {
  const [documents, setDocuments] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [search, setSearch] = useState('');
  const [mandateHas404, setMandateHas404] = useState(false);
  const prospectId = typeof window !== 'undefined' ? localStorage.getItem('currentProspectId') : null;
  const propertyId = typeof window !== 'undefined' ? localStorage.getItem('currentPropertyId') || documentPropertyById : documentPropertyById;

  const brokerQueryArgs = type === 'BROKER' && (prospectId || brokerId) ? { brokerId: prospectId || brokerId, search } : null;

  const {
    data: brokerDocumentsData,
    isLoading: isBrokerLoading,
    error: brokerError,
    refetch: refetchBroker,
  } = useGetDocumentsQuery(brokerQueryArgs, {
    skip: type !== 'BROKER' || !brokerQueryArgs,
  });

  const {
    data: propertyDocumentsData,
    isLoading: isPropertyLoading,
    error: propertyError,
    refetch: refetchProperty,
  } = useGetAllPropertyDocumentsQuery(type === 'PROPERTY' || type === 'EXCLUSIVE' ? { propertyId, search } : null, {
    skip: type !== 'PROPERTY' && type !== 'EXCLUSIVE',
  });

  const currentData = type === 'BROKER' ? brokerDocumentsData : propertyDocumentsData;
  const currentLoading = type === 'BROKER' ? isBrokerLoading : isPropertyLoading;
  const currentError = type === 'BROKER' ? brokerError : propertyError;
  const currentRefetch = type === 'BROKER' ? refetchBroker : refetchProperty;

  useEffect(() => {
    const apiContent = currentData?.data?.content;

    if (!Array.isArray(apiContent)) {
      setDocuments({ content: [] });
      setSelectedDoc(null);
      onDocumentsChange(false);
      return;
    }

    const content = apiContent;
    setDocuments(currentData.data);

    const hasUploaded = content.some((doc) => {
      const status = (doc.status || (doc.fileUrl ? 'UPLOADED' : 'PENDING')).toString().toUpperCase();
      return status === 'UPLOADED' && !!doc.fileUrl;
    });

    // Prefer selecting a Mandate document if present (even without fileUrl), including PENDING mandates
    const firstMandate = content.find((doc) => {
      const label = `${doc.type || doc.documentType || doc.name || ''}`.toLowerCase();
      return label.includes('mandate');
    });
    const firstPreviewable = content.find((doc) => doc.fileUrl) || null;
    setSelectedDoc(firstMandate || firstPreviewable);
    onDocumentsChange(hasUploaded);
  }, [currentData, onDocumentsChange, type]);

  const handleSelectDocument = (doc) => {
    setSelectedDoc(doc);
  };

  const handleDocumentsChange = (hasDocuments) => {
    // Don't clear selected document if it's a mandate (even if no uploaded documents)
    if (!hasDocuments && !selectedDoc?.isMandate && !`${selectedDoc?.type || selectedDoc?.documentType || selectedDoc?.name || ''}`.toLowerCase().includes('mandate')) {
      setSelectedDoc(null);
    }
  };

  if (isPropertyLoading)
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-orange border-t-transparent" />
      </div>
    );

  // Check if selected document is a Mandate type and has 404 error
  const isMandateDocument = (() => {
    if (!selectedDoc) return false;
    if (!mandateHas404) return false;
    const status = selectedDoc.status.toString().toUpperCase();
    if (status === 'UPLOADED') return false;
    if (selectedDoc.isMandate) return true;
    const label = `${selectedDoc.type || selectedDoc.documentType || selectedDoc.name || ''}`.toLowerCase();
    return label.includes('mandate');
  })();

  // const anyMandateInList = false; // Only show MandateWorkflow for selected mandate document

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
      <DocumentsList
        methods={methods}
        onSubmit={onSubmit}
        prospectId={prospectId}
        documents={documents}
        loading={currentLoading}
        error={currentError}
        onSelectDocument={handleSelectDocument}
        onDocumentsChange={handleDocumentsChange}
        onDocumentDeleted={() => {
          setSelectedDoc(null);
          currentRefetch();
        }}
        refetchDocuments={currentRefetch}
        onSearchChange={setSearch}
        documentPropertyById={documentPropertyById}
        type={type}
        sampleType={sampleType}
        brokerId={brokerId}
        onMandateStatus={setMandateHas404}
      />

      {isMandateDocument ? (
        <div className="md:col-span-3">
          <MandateWorkflow propertyId={propertyId} />
        </div>
      ) : (
        <Preview src={selectedDoc?.fileUrl} />
      )}
    </div>
  );
};

export default DocumentsAndPreview;
